import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/lib/api-client';
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  FunnelIcon,
  ArrowUpTrayIcon,
  StarIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { cn } from '@/utils/cn';

interface Contact {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company_name: string;
  job_title: string;
  contact_type: string;
  contact_status: string;
  flag: string;
  flag_reason: string;
  is_mrvhub_customer: boolean;
  important_dates: Record<string, string>;
  tags: string[];
  notes: string;
  last_contacted_at: string;
  total_calls: number;
  total_emails: number;
  created_at: string;
  updated_at: string;
}

interface CRMStats {
  total: number;
  customers: number;
  prospects: number;
  salespeople: number;
  vendors: number;
  vips: number;
  nuisances: number;
  blocked: number;
  mrvhub_customers: number;
}

const typeColors: Record<string, string> = {
  customer: 'bg-emerald-100 text-emerald-700',
  prospect: 'bg-blue-100 text-blue-700',
  vendor: 'bg-purple-100 text-purple-700',
  partner: 'bg-indigo-100 text-indigo-700',
  salesperson: 'bg-orange-100 text-orange-700',
  internal: 'bg-gray-100 text-gray-700',
  other: 'bg-gray-100 text-gray-600',
};

const flagIcons: Record<string, { icon: any; color: string; label: string }> = {
  vip: { icon: StarIconSolid, color: 'text-amber-500', label: 'VIP' },
  nuisance: { icon: ExclamationTriangleIcon, color: 'text-orange-500', label: 'Nuisance' },
  blocked: { icon: NoSymbolIcon, color: 'text-red-500', label: 'Blocked' },
};

const emptyContact = {
  first_name: '', last_name: '', email: '', phone: '', company_name: '',
  job_title: '', contact_type: 'other', contact_status: 'active',
  flag: 'none', flag_reason: '', is_mrvhub_customer: false,
  important_dates: {}, tags: [], notes: '',
};

export function CRMPage() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [stats, setStats] = useState<CRMStats | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [flagFilter, setFlagFilter] = useState('');
  const [mrvhubFilter, setMrvhubFilter] = useState<boolean | undefined>();
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [formData, setFormData] = useState<any>(emptyContact);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [interactions, setInteractions] = useState<any[]>([]);
  const [tickets, setTickets] = useState<any[]>([]);
  const [showImport, setShowImport] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { limit: 50, offset: 0 };
      if (search) params.search = search;
      if (typeFilter) params.contact_type = typeFilter;
      if (flagFilter) params.flag = flagFilter;
      if (mrvhubFilter !== undefined) params.is_mrvhub_customer = mrvhubFilter;
      
      const res = await apiClient.get('/contacts', { params });
      setContacts(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, flagFilter, mrvhubFilter]);

  const fetchStats = async () => {
    try {
      const res = await apiClient.get('/contacts/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => { fetchContacts(); fetchStats(); }, [fetchContacts]);

  const handleSave = async () => {
    try {
      if (editingContact) {
        await apiClient.put(`/contacts/${editingContact.id}`, formData);
      } else {
        await apiClient.post('/contacts', formData);
      }
      setShowForm(false);
      setEditingContact(null);
      setFormData(emptyContact);
      fetchContacts();
      fetchStats();
    } catch (err) {
      console.error('Failed to save contact:', err);
    }
  };

  const handleEdit = (contact: Contact) => {
    setEditingContact(contact);
    setFormData({ ...contact });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await apiClient.delete(`/contacts/${id}`);
      fetchContacts();
      fetchStats();
      if (selectedContact?.contact?.id === id) setSelectedContact(null);
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleViewContact = async (contact: Contact) => {
    try {
      const res = await apiClient.get(`/contacts/${contact.id}`);
      setSelectedContact(res.data);
    } catch (err) {
      console.error('Failed to fetch contact detail:', err);
    }
  };

  const handleImport = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fileInput = form.querySelector('input[type="file"]') as HTMLInputElement;
    if (!fileInput?.files?.[0]) return;
    
    const data = new FormData();
    data.append('file', fileInput.files[0]);
    
    try {
      const res = await apiClient.post('/contacts/import', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      fetchContacts();
      fetchStats();
    } catch (err) {
      console.error('Import failed:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CRM</h1>
          <p className="text-sm text-gray-500">Contacts, pipeline & customer intelligence</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowImport(!showImport)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ArrowUpTrayIcon className="w-4 h-4" />
            Import CSV
          </button>
          <button
            onClick={() => { setEditingContact(null); setFormData(emptyContact); setShowForm(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          >
            <PlusIcon className="w-4 h-4" />
            Add Contact
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {[
            { label: 'Total', value: stats.total, color: 'text-gray-900' },
            { label: 'Customers', value: stats.customers, color: 'text-emerald-600' },
            { label: 'Prospects', value: stats.prospects, color: 'text-blue-600' },
            { label: 'MRVHub', value: stats.mrvhub_customers, color: 'text-indigo-600' },
            { label: 'VIPs', value: stats.vips, color: 'text-amber-600' },
            { label: 'Blocked', value: stats.blocked, color: 'text-red-600' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-lg border border-gray-200 p-3 text-center">
              <p className={cn('text-2xl font-bold', s.color)}>{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* CSV Import */}
      {showImport && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="font-medium text-gray-900 mb-3">Import Contacts from CSV</h3>
          <form onSubmit={handleImport} className="flex items-center gap-3">
            <input type="file" accept=".csv,.CSV" className="text-sm" />
            <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700">
              Upload
            </button>
          </form>
          {importResult && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
              <p>✅ Created: {importResult.created} | Updated: {importResult.updated} | Skipped: {importResult.skipped} | Errors: {importResult.total_errors}</p>
            </div>
          )}
        </div>
      )}

      {/* Search & Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search contacts..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white">
          <option value="">All Types</option>
          {['customer','prospect','vendor','partner','salesperson','internal','other'].map(t => (
            <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
          ))}
        </select>
        <select value={flagFilter} onChange={e => setFlagFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white">
          <option value="">All Flags</option>
          <option value="vip">⭐ VIP</option>
          <option value="nuisance">⚠️ Nuisance</option>
          <option value="blocked">🚫 Blocked</option>
        </select>
        <button
          onClick={() => setMrvhubFilter(mrvhubFilter === true ? undefined : true)}
          className={cn(
            'px-3 py-2 text-sm rounded-lg border',
            mrvhubFilter === true ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-white border-gray-300 text-gray-700'
          )}
        >
          MRVHub Only
        </button>
      </div>

      <div className="flex gap-6">
        {/* Contacts Table */}
        <div className={cn('flex-1 bg-white rounded-lg border border-gray-200 overflow-hidden', selectedContact ? 'w-1/2' : 'w-full')}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Company</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Flag</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Contact</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
                ) : contacts.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No contacts found</td></tr>
                ) : (
                  contacts.map(c => (
                    <tr key={c.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleViewContact(c)}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {c.is_mrvhub_customer && <CheckBadgeIcon className="w-4 h-4 text-indigo-500" title="MRVHub Customer" />}
                          <div>
                            <p className="font-medium text-gray-900">{c.first_name} {c.last_name}</p>
                            {c.job_title && <p className="text-xs text-gray-500">{c.job_title}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{c.company_name || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', typeColors[c.contact_type] || typeColors.other)}>
                          {c.contact_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {c.flag !== 'none' && flagIcons[c.flag] && (() => {
                          const f = flagIcons[c.flag];
                          return (
                            <span className="inline-flex items-center gap-1" title={c.flag_reason || c.flag}>
                              <f.icon className={cn('w-4 h-4', f.color)} />
                              <span className="text-xs">{f.label}</span>
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3 text-gray-400">
                          {c.phone && <PhoneIcon className="w-4 h-4" title={c.phone} />}
                          {c.email && <EnvelopeIcon className="w-4 h-4" title={c.email} />}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={(e) => { e.stopPropagation(); handleEdit(c); }}
                          className="text-xs text-primary-600 hover:text-primary-700 mr-2">Edit</button>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }}
                          className="text-xs text-red-500 hover:text-red-700">Delete</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-gray-200 text-xs text-gray-500">
            Showing {contacts.length} of {total} contacts
          </div>
        </div>

        {/* Contact Detail Panel */}
        {selectedContact && (
          <div className="w-96 bg-white rounded-lg border border-gray-200 overflow-y-auto max-h-[80vh]">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">
                  {selectedContact.contact.first_name} {selectedContact.contact.last_name}
                </h3>
                <button onClick={() => setSelectedContact(null)} className="text-gray-400 hover:text-gray-600">✕</button>
              </div>
              {selectedContact.contact.company_name && (
                <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                  <BuildingOfficeIcon className="w-4 h-4" />
                  {selectedContact.contact.company_name}
                </p>
              )}
              <div className="flex gap-2 mt-2">
                <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', typeColors[selectedContact.contact.contact_type] || typeColors.other)}>
                  {selectedContact.contact.contact_type}
                </span>
                {selectedContact.contact.is_mrvhub_customer && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">MRVHub ✓</span>
                )}
              </div>
            </div>

            {/* Contact Info */}
            <div className="p-4 space-y-2 border-b border-gray-100 text-sm">
              {selectedContact.contact.email && (
                <p className="flex items-center gap-2 text-gray-600">
                  <EnvelopeIcon className="w-4 h-4 text-gray-400" />
                  <a href={`mailto:${selectedContact.contact.email}`} className="hover:text-primary-600">{selectedContact.contact.email}</a>
                </p>
              )}
              {selectedContact.contact.phone && (
                <p className="flex items-center gap-2 text-gray-600">
                  <PhoneIcon className="w-4 h-4 text-gray-400" />
                  <a href={`tel:${selectedContact.contact.phone}`} className="hover:text-primary-600">{selectedContact.contact.phone}</a>
                </p>
              )}
              {selectedContact.contact.job_title && (
                <p className="text-gray-500">{selectedContact.contact.job_title}</p>
              )}
            </div>

            {/* Important Dates */}
            {selectedContact.contact.important_dates && Object.keys(selectedContact.contact.important_dates).length > 0 && (
              <div className="p-4 border-b border-gray-100">
                <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Important Dates</h4>
                {Object.entries(selectedContact.contact.important_dates).map(([key, val]) => (
                  <p key={key} className="text-sm text-gray-600">
                    <span className="font-medium">{key.replace(/_/g, ' ')}:</span> {val as string}
                  </p>
                ))}
              </div>
            )}

            {/* Notes */}
            {selectedContact.contact.notes && (
              <div className="p-4 border-b border-gray-100">
                <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Notes</h4>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedContact.contact.notes}</p>
              </div>
            )}

            {/* Interactions */}
            <div className="p-4 border-b border-gray-100">
              <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">
                Recent Activity ({selectedContact.interactions?.length || 0})
              </h4>
              {selectedContact.interactions?.length > 0 ? (
                <div className="space-y-2">
                  {selectedContact.interactions.slice(0, 10).map((int: any) => (
                    <div key={int.id} className="text-sm border-l-2 border-gray-200 pl-3">
                      <p className="text-gray-900 font-medium">{int.subject || int.interaction_type}</p>
                      {int.content && <p className="text-gray-500 text-xs line-clamp-2">{int.content}</p>}
                      <p className="text-gray-400 text-xs">{new Date(int.created_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">No interactions yet</p>
              )}
            </div>

            {/* Related Tickets */}
            {selectedContact.tickets?.length > 0 && (
              <div className="p-4">
                <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">
                  Support Tickets ({selectedContact.tickets.length})
                </h4>
                <div className="space-y-2">
                  {selectedContact.tickets.map((t: any) => (
                    <div key={t.id} className="text-sm">
                      <p className="text-gray-900">#{t.ticket_number} — {t.title}</p>
                      <p className="text-xs text-gray-400">{t.status} · {t.priority}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold">{editingContact ? 'Edit Contact' : 'New Contact'}</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input value={formData.first_name || ''} onChange={e => setFormData({...formData, first_name: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input value={formData.last_name || ''} onChange={e => setFormData({...formData, last_name: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input value={formData.phone || ''} onChange={e => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <input value={formData.company_name || ''} onChange={e => setFormData({...formData, company_name: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                <input value={formData.job_title || ''} onChange={e => setFormData({...formData, job_title: e.target.value})}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={formData.contact_type} onChange={e => setFormData({...formData, contact_type: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white">
                    {['customer','prospect','vendor','partner','salesperson','internal','other'].map(t => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Flag</label>
                  <select value={formData.flag} onChange={e => setFormData({...formData, flag: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white">
                    <option value="none">None</option>
                    <option value="vip">⭐ VIP</option>
                    <option value="nuisance">⚠️ Nuisance</option>
                    <option value="blocked">🚫 Blocked</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 pb-2">
                    <input type="checkbox" checked={formData.is_mrvhub_customer || false}
                      onChange={e => setFormData({...formData, is_mrvhub_customer: e.target.checked})}
                      className="rounded border-gray-300" />
                    <span className="text-sm text-gray-700">MRVHub Customer</span>
                  </label>
                </div>
              </div>
              {formData.flag !== 'none' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Flag Reason</label>
                  <input value={formData.flag_reason || ''} onChange={e => setFormData({...formData, flag_reason: e.target.value})}
                    placeholder="e.g. Persistent cold caller"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})}
                  rows={3} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => { setShowForm(false); setEditingContact(null); }}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave}
                className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700">
                {editingContact ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CRMPage;
